/**
 * Create / edit dialog for a Credential.
 *
 * Form auto-generated from the type's JSON Schemas (config + secrets).
 * When editing, secrets are NEVER pre-filled (the backend doesn't
 * return them); leaving secret fields blank on save keeps existing
 * values, filling them rotates.
 *
 * Three sections:
 *   1. Type picker (create only — immutable after create)
 *   2. Name + description + tags
 *   3. Type-specific config + secret fields (auto-rendered)
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import * as React from "react";

import { TagsField } from "@/components/tags";
import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  credentialsApi,
  type Credential,
  type CredentialType,
  type CredentialTypeInfo,
} from "@/lib/api-credentials";
import { gatewaysApi } from "@/lib/api-gateways";


export type DialogMode =
  | { kind: "create"; type?: CredentialType }
  | { kind: "edit"; credential: Credential };


export interface CredentialDialogProps {
  open: boolean;
  mode: DialogMode;
  onOpenChange: (open: boolean) => void;
  /** Create mode: reopen the type chooser ("← Change type"). */
  onChangeType?: () => void;
}


export function CredentialDialog({ open, mode, onOpenChange, onChangeType }: CredentialDialogProps) {
  const qc = useQueryClient();
  const isCreate = mode.kind === "create";
  const editTarget = mode.kind === "edit" ? mode.credential : null;
  //: The type chosen in the pre-modal chooser (create) — defaults to smtp.
  const createType: CredentialType = (mode.kind === "create" && mode.type) || "smtp";

  const typesQ = useQuery({
    queryKey: ["credentials", "types"],
    queryFn: credentialsApi.types,
    enabled: open,
  });

  const [type, setType] = React.useState<CredentialType>(
    editTarget?.type ?? createType,
  );
  const [name, setName] = React.useState(editTarget?.name ?? "");
  const [description, setDescription] = React.useState(editTarget?.description ?? "");
  const [tags, setTags] = React.useState<string[]>(editTarget?.tags ?? []);
  const [enabled, setEnabled] = React.useState(editTarget?.enabled ?? true);
  const [config, setConfig] = React.useState<Record<string, unknown>>(
    editTarget?.config ?? {},
  );
  const [secrets, setSecrets] = React.useState<Record<string, string>>({});

  React.useEffect(() => {
    if (!open) return;
    if (editTarget) {
      setType(editTarget.type);
      setName(editTarget.name);
      setDescription(editTarget.description ?? "");
      setTags(editTarget.tags ?? []);
      setEnabled(editTarget.enabled);
      setConfig(editTarget.config ?? {});
      setSecrets({});  // never pre-fill secrets — always blank on edit
    } else {
      setType(createType);
      setName("");
      setDescription("");
      setTags([]);
      setEnabled(true);
      setConfig({});
      setSecrets({});
    }
  }, [open, editTarget, createType]);

  const typeInfo: CredentialTypeInfo | undefined = React.useMemo(
    () => (typesQ.data ?? []).find((t) => t.type_id === type),
    [typesQ.data, type],
  );

  // When type changes (only possible during create), clear config +
  // secrets — fields differ across types.
  const prevType = React.useRef(type);
  React.useEffect(() => {
    if (isCreate && prevType.current !== type) {
      setConfig({});
      setSecrets({});
    }
    prevType.current = type;
  }, [type, isCreate]);

  const submit = useMutation<unknown, Error>({
    mutationFn: async () => {
      if (isCreate) {
        return credentialsApi.create({
          type,
          name: name.trim(),
          description: description.trim() || null,
          tags,
          enabled,
          config,
          secrets,
        });
      }
      // Edit: split into two calls — config update is one PATCH;
      // secrets rotation is a separate POST. We skip the latter if
      // the user didn't touch any secret field.
      const target = editTarget!;
      const configChanged =
        name.trim() !== target.name ||
        (description.trim() || null) !== target.description ||
        enabled !== target.enabled ||
        JSON.stringify(tags) !== JSON.stringify(target.tags) ||
        JSON.stringify(config) !== JSON.stringify(target.config);
      const secretsToRotate = Object.fromEntries(
        Object.entries(secrets).filter(([, v]) => v !== ""),
      );
      const tasks: Promise<unknown>[] = [];
      if (configChanged) {
        tasks.push(
          credentialsApi.update(target.id, {
            name: name.trim(),
            description: description.trim() || null,
            tags,
            enabled,
            config,
          }),
        );
      }
      if (Object.keys(secretsToRotate).length > 0) {
        tasks.push(credentialsApi.rotateSecrets(target.id, secretsToRotate));
      }
      return Promise.all(tasks);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["credentials"] });
      onOpenChange(false);
    },
  });

  const canSubmit = name.trim().length > 0 && !submit.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle className="text-sm font-semibold">
            {isCreate ? "New credential" : `Edit ${editTarget?.name}`}
          </DialogTitle>
        </DialogHeader>
        <DialogBody>
          {/* Type — chosen in the pre-modal chooser; immutable after create. */}
          {isCreate && (
            <div className="flex items-center justify-between rounded-md border border-border bg-surface px-3 py-2">
              <div className="min-w-0">
                <div className="text-sm font-medium">
                  {typeInfo?.label ?? type}
                </div>
                {typeInfo?.description && (
                  <div className="truncate text-xs text-muted-foreground">
                    {typeInfo.description}
                  </div>
                )}
              </div>
              {onChangeType && (
                <Button variant="ghost" size="sm" onClick={onChangeType} className="shrink-0">
                  Cambia tipo
                </Button>
              )}
            </div>
          )}

          {/* Name + description */}
          <div className="space-y-1.5">
            <Label htmlFor="cred-name">Name</Label>
            <Input
              id="cred-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Gmail SMTP Ops"
              autoFocus
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="cred-desc">Description (optional)</Label>
            <Input
              id="cred-desc"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="What is this credential for?"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="cred-tags">Tags</Label>
            <TagsField id="cred-tags" value={tags} onChange={setTags} />
          </div>

          {/* Config (auto from schema) */}
          {typeInfo && (
            <div className="border-t border-border/60 pt-3">
              <div className="mb-2 text-xs uppercase tracking-wider text-muted-foreground">
                Configuration
              </div>
              <SchemaForm
                schema={typeInfo.config_schema}
                values={config}
                onChange={setConfig}
              />
            </div>
          )}

          {/* Secrets (auto from schema, write-only) */}
          {typeInfo && (
            <div className="border-t border-border/60 pt-3">
              <div className="mb-2 text-xs uppercase tracking-wider text-muted-foreground">
                Secrets {!isCreate && "(leave empty to keep current)"}
              </div>
              <SchemaForm
                schema={typeInfo.secrets_schema}
                values={secrets}
                onChange={(v) => setSecrets(v as Record<string, string>)}
                allSecret
              />
            </div>
          )}

          <label className="flex items-center gap-2 pt-1 text-sm">
            <Switch checked={enabled} onCheckedChange={setEnabled} />
            <span>Enabled</span>
          </label>

          {submit.error && (
            <Alert tone="danger" title="Save failed">
              {(submit.error as Error).message}
            </Alert>
          )}
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" size="sm" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            size="sm"
            disabled={!canSubmit}
            onClick={() => submit.mutate()}
          >
            {submit.isPending && (
              <Loader2 className="h-3.5 w-3.5 animate-spin" strokeWidth={1.5} />
            )}
            {isCreate ? "Create credential" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// ---------------------------------------------------------------------------
// Auto-form from JSON Schema (subset — only the shapes pydantic emits)
// ---------------------------------------------------------------------------

interface SchemaProperty {
  type?: string;
  description?: string;
  default?: unknown;
  enum?: unknown[];
  pattern?: string;
  minimum?: number;
  maximum?: number;
  anyOf?: Array<{ type?: string }>;
  title?: string;
  writeOnly?: boolean;
  /** Backend marker (pydantic `json_schema_extra`) → render a smart picker instead of a
   *  free-text input. `"gateway"` = a gateway dropdown; `"gateway_remote"` = the live
   *  connection-names of the chosen gateway (ADR-0042 slice 7, the `tray_gateway` cred). */
  "x-tray-picker"?: string;
}

interface SchemaShape {
  properties?: Record<string, SchemaProperty>;
  required?: string[];
}

function SchemaForm({
  schema,
  values,
  onChange,
  allSecret = false,
}: {
  schema: SchemaShape;
  values: Record<string, unknown>;
  onChange: (next: Record<string, unknown>) => void;
  allSecret?: boolean;
}) {
  const props = schema.properties ?? {};
  const required = new Set(schema.required ?? []);

  function setField(key: string, value: unknown) {
    onChange({ ...values, [key]: value });
  }

  return (
    <div className="space-y-3">
      {Object.entries(props).map(([key, p]) => {
        const isSecret =
          allSecret ||
          p.writeOnly ||
          // pydantic SecretStr lands as ``"format": "password"`` in
          // older Pydantic versions, or as a $ref to SecretStr in
          // newer ones — both are presented as masked input here.
          (p as { format?: string }).format === "password";
        const inputType = isSecret ? "password" : p.type === "integer" ? "number" : "text";
        const isBool = p.type === "boolean";
        const enumValues = Array.isArray(p.enum) ? p.enum : null;
        const picker = p["x-tray-picker"];
        const current = values[key] ?? p.default ?? "";

        return (
          <div key={key} className="space-y-1">
            <Label htmlFor={`f-${key}`}>
              {p.title ?? key}
              {required.has(key) && <span className="ml-1 text-danger">*</span>}
            </Label>
            {picker === "gateway" ? (
              <GatewayPicker
                id={`f-${key}`}
                value={current === null ? "" : String(current)}
                onChange={(v) => setField(key, v)}
              />
            ) : picker === "gateway_remote" ? (
              <GatewayRemotePicker
                id={`f-${key}`}
                gatewayId={String(values.gateway_id ?? "")}
                value={current === null ? "" : String(current)}
                onChange={(v) => setField(key, v)}
              />
            ) : isBool ? (
              <Switch
                checked={Boolean(current)}
                onCheckedChange={(v) => setField(key, v)}
              />
            ) : enumValues ? (
              <select
                id={`f-${key}`}
                value={String(current)}
                onChange={(e) => setField(key, e.target.value)}
                className="block w-full rounded-md border border-border bg-surface px-2 py-1.5 text-sm"
              >
                {enumValues.map((v) => (
                  <option key={String(v)} value={String(v)}>{String(v)}</option>
                ))}
              </select>
            ) : (
              <Input
                id={`f-${key}`}
                type={inputType}
                value={current === null ? "" : String(current)}
                onChange={(e) => {
                  const v = e.target.value;
                  setField(key, p.type === "integer" ? (v === "" ? null : Number(v)) : v);
                }}
                placeholder={isSecret ? "•••••••• (write-only)" : undefined}
              />
            )}
            {p.description && (
              <p className="text-[11px] text-muted-foreground">{p.description}</p>
            )}
          </div>
        );
      })}
    </div>
  );
}


// ---------------------------------------------------------------------------
// Tray Gateway pickers (ADR-0042 slice 7) — the `tray_gateway` credential is a
// pointer; both its fields are chosen from live data, never typed by hand.
// ---------------------------------------------------------------------------

const selectClass =
  "block w-full rounded-md border border-border bg-surface px-2 py-1.5 text-sm";

/** Dropdown of the registered gateways (value = gateway id, label = name). */
export function GatewayPicker({
  id,
  value,
  onChange,
}: {
  id: string;
  value: string;
  onChange: (v: string) => void;
}) {
  const q = useQuery({
    queryKey: ["gateways", "picker"],
    queryFn: () => gatewaysApi.list(),
    staleTime: 30_000,
  });
  const gateways = q.data ?? [];
  return (
    <select id={id} value={value} onChange={(e) => onChange(e.target.value)} className={selectClass}>
      <option value="">{q.isLoading ? "loading…" : "Select a gateway…"}</option>
      {gateways.map((g) => (
        <option key={g.id} value={g.id}>
          {g.name}
          {g.status !== "online" ? ` (${g.status})` : ""}
        </option>
      ))}
    </select>
  );
}

/** Dropdown of the connection names the CHOSEN gateway's agent holds. Disabled until a
 *  gateway is picked; shows "agent offline" when no agent is connected (the cloud can't
 *  enumerate connections then) and falls back to a free-text entry so a name can still be
 *  set out-of-band. */
export function GatewayRemotePicker({
  id,
  gatewayId,
  value,
  onChange,
}: {
  id: string;
  gatewayId: string;
  value: string;
  onChange: (v: string) => void;
}) {
  const q = useQuery({
    queryKey: ["gateways", "remotes", gatewayId],
    queryFn: () => gatewaysApi.remotes(gatewayId),
    enabled: Boolean(gatewayId),
    staleTime: 10_000,
  });

  if (!gatewayId) {
    return (
      <select id={id} disabled className={selectClass}>
        <option>Select a gateway first…</option>
      </select>
    );
  }
  if (q.isLoading) {
    return (
      <select id={id} disabled className={selectClass}>
        <option>loading…</option>
      </select>
    );
  }
  // Offline (or revoked): the cloud can't list connections — let the operator type the name.
  if (!q.data?.online) {
    return (
      <div className="space-y-1">
        <Input id={id} value={value} onChange={(e) => onChange(e.target.value)} placeholder="connection name" />
        <p className="text-[11px] text-amber-500">
          Agent offline — names can't be listed. Type the connection name manually.
        </p>
      </div>
    );
  }
  const remotes = q.data.remotes;
  return (
    <select id={id} value={value} onChange={(e) => onChange(e.target.value)} className={selectClass}>
      <option value="">Select a connection…</option>
      {remotes.map((r) => (
        <option key={r.ref} value={r.ref}>
          {r.ref}
        </option>
      ))}
      {/* Preserve an already-saved value the agent no longer reports (renamed/removed). */}
      {value && !remotes.some((r) => r.ref === value) && (
        <option value={value}>{value} (not currently reported)</option>
      )}
    </select>
  );
}
